/**
 * 1.6.0 AI content description: Administration → Webhooks → "Add from a template", the AI providers' endpoints
 * (AI_FLOW_TEMPLATES' endpoint: Claude, OpenAI, Gemini, OpenRouter; OpenRouter's URL also fits a local Ollama or
 * vLLM). Picking one opens the new-endpoint dialog filled in (onPick: name, URL, method, the API key header's name
 * with its prefix as the value for the administrator to complete, the provider's fixed headers, media links on) and
 * the description carries the provider's data note. Stream A's file (WebhooksAdmin.tsx only mounts it).
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Sparkles } from 'lucide-react';
import { AI_FLOW_TEMPLATES, type AiFlowTemplate } from '@prism/contracts';
import type { EndpointPreset } from '../WebhooksAdmin';

export function endpointPreset(t: AiFlowTemplate): EndpointPreset {
  const e = t.endpoint;
  return {
    name: e.name, url: e.url, method: e.method, description: `${e.description} ${t.dataNote}`.slice(0, 500),
    headers: [{ name: e.apiKeyHeader, value: e.apiKeyPrefix }, ...Object.entries(e.headers).map(([name, value]) => ({ name, value }))],
    mayReceiveMediaLinks: true,
  };
}

export function AiEndpointTemplates({ onPick }: { onPick: (preset: EndpointPreset) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [open]);
  return (
    <div className="ai-template-menu" ref={ref}>
      <button className="btn" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="endpoint-from-template">
        <Sparkles size={15} /> Add from a template <ChevronDown size={13} />
      </button>
      {open && (
        <div className="ai-template-menu-list" role="menu">
          {AI_FLOW_TEMPLATES.map((t) => (
            <button key={t.id} role="menuitem" className="ai-template-menu-item" data-testid="endpoint-template" data-provider={t.provider}
              onClick={() => { setOpen(false); onPick(endpointPreset(t)); }}>
              <strong>{t.endpoint.name}</strong>
              <span className="faint mono">{t.endpoint.url}</span>
              <span className="faint">The API key goes in the {t.endpoint.apiKeyHeader} header{t.endpoint.apiKeyPrefix ? ` after “${t.endpoint.apiKeyPrefix.trim()}”` : ''}.</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
